import React, { useState, useRef, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import "./SuperAgentChat.scss";
import aiChatlogo from "../../assets/Images/aiChatlogo.png";
import { AIService, abortCurrentRequest } from "../../api/services/ai.service";
import {
  PlanMyDayDummyMessages,
  HighPriorityOrdersDummyMessages,
  MandatoryFieldsJEDummyMessages,
  ReviewerUSDummyMessages,
  OrdersInDataTriageDummyMessages,
  NewlyAllocatedOrdersDummyMessages,
} from "./SuperAgentChatDummyData";
import { SuperChatAI } from "../Chats/SuperChat/SuperChatAI/SuperChatAI";
import { SuperChatUser } from "../Chats/SuperChat/SuperChatUser/SuperChatUser";
import { OrdersService } from "../../api/services/orders.service";
import { getAIMessageText } from "../Chats/SuperChat/SuperChatAI/SuperChatAI.constants";
import { CHAT, CHAT_MESSAGES, ARIA_LABELS } from "../../util/constants";
import { selectSelectedRegion } from "../../store/slices/regionSlice";
import { setAllowedOrderIds } from "../../store/slices/userSlice";

// Map mode to static_view_type
const modeToStaticViewType = {
  planMyDay: "plan_my_day",
  ordersInDataTriage: "orders_data_triage",
  newlyAllocatedOrders: "newly_allocated_orders",
};

const staticViewTypes = [
  "plan_my_day",
  "orders_data_triage",
  "newly_allocated_orders",
  "high_iteration_orders",
  "aging_orders",
  "pending_review",
  "my_weekly_orders",
  "awaiting_response_orders",
  "awaiting_at_response",
  "orders_near_duedate",
  "allocation_review_request",
  "action_needed_orders",
  "regional_leads_weekly_orders",
];

// Create a constant outside component
const MODE_TO_MESSAGES = {
  highPriorityOrders: HighPriorityOrdersDummyMessages,
  ordersInDataTriage: OrdersInDataTriageDummyMessages,
  newlyAllocatedOrders: NewlyAllocatedOrdersDummyMessages,
  mandatoryFieldsJE: MandatoryFieldsJEDummyMessages,
  reviewerUS: ReviewerUSDummyMessages,
  planMyDay: PlanMyDayDummyMessages,
};

// Usage

// Role-based suggestions mapping - defines which direct API calls are allowed for each role
// If a user types a suggestion not in their role's list, it will go to AI query API
const ROLE_ALLOWED_DIRECT_SUGGESTIONS = {
  "REGIONAL LEAD": [
    "order by status",
    "order creation pending",
    "allocation review requested",
  ],
  DID: [
    // DID doesn't have "order by status" or "order creation pending"
    // These will go through AI query API if DID user types them
  ],
  // Add other roles as needed
};

// Helper function to check if a suggestion is allowed for direct API call based on role
const isSuggestionAllowedForRole = (text, roleName) => {
  const normalizedText = String(text || "").toLowerCase().trim();
  const normalizedRoleName = String(roleName || "").toUpperCase().trim();
  
  const allowedSuggestions = ROLE_ALLOWED_DIRECT_SUGGESTIONS[normalizedRoleName] || [];
  return allowedSuggestions.includes(normalizedText);
};

// Chat interface for super agent with dynamic message loading based on mode
const SuperAgentChat = ({
  onSendMessage,
  mode = "planMyDay",
  onNewChatRequest,
  startFresh = false,
  onLoadingChange,
}) => {
  const selectedRegion = useSelector(selectSelectedRegion);
  const navigate = useNavigate();
  const user = useSelector((state) => state.user.user);
  const selectedRole = useSelector((state) => state.user.selectedRole);
  const userName = user?.name?.split(" ")[0] || "User";

  const initialMessages = MODE_TO_MESSAGES[mode] || PlanMyDayDummyMessages;
  const [messages, setMessages] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [planMyDayOrders, setPlanMyDayOrders] = useState(null);

  // Checkpoint state management
  const [checkpointState, setCheckpointState] = useState({
    messageId: null,
    isSubmitting: false,
    submitError: null,
    submitSuccess: false,
  });

  const listRef = useRef(null);
  const dispatch = useDispatch();
  // Ref to track animation timeout for cleanup
  const animationTimeoutRef = useRef(null);

  // Word-by-word animation function
  const animateTextWordByWord = useCallback((fullText, messageId) => {
    // Clear any existing animation
    if (animationTimeoutRef.current) {
      clearTimeout(animationTimeoutRef.current);
    }

    const words = fullText.split(/(\s+)/); // Split keeping whitespace
    let currentIndex = 0;
    let displayedText = "";

    const animateNextWord = () => {
      if (currentIndex < words.length) {
        displayedText += words[currentIndex];
        currentIndex++;

        setMessages((prev) =>
          prev.map((msg) =>
            msg.id === messageId ? { ...msg, text: displayedText } : msg,
          ),
        );

        // Continue animation with 20ms delay per word
        animationTimeoutRef.current = setTimeout(animateNextWord, 20);
      }
    };

    // Start animation immediately
    animateNextWord();
  }, []);

  // Notify parent when loading state changes (includes checkpoint submitting)
  useEffect(() => {
    if (onLoadingChange) {
      onLoadingChange(isLoading || checkpointState.isSubmitting);
    }
  }, [isLoading, checkpointState.isSubmitting, onLoadingChange]);

  const userEmail =
    user?.email ||
    user?.mail ||
    user?.username ||
    user?.preferred_username ||
    user?.upn ||
    "";

  // Fetches AI-allowed order IDs for a role and dispatches them to the store.
  // Scenario 3: Called when getOrdersByType or getOrderByStatus APIs are invoked
  // Returns silently on error so callers are never blocked by this call.
  const fetchAndDispatchAIOrders = async (roleId) => {
    if (!roleId) return;
    try {
      const aiOrdersData = await OrdersService.getAIOrders(roleId);
      // getAIOrders returns response.data directly – handle both array and
      // object-with-orderIds shapes.
      const rawOrderIds = Array.isArray(aiOrdersData)
        ? aiOrdersData
        : aiOrdersData?.data?.orderIds || aiOrdersData?.orderIds || [];
      const orderIds = rawOrderIds.map((id) => String(id));
      dispatch(setAllowedOrderIds(orderIds));
    } catch (aiErr) {
      console.warn("Failed to fetch AI order IDs:", aiErr);
    }
  };

  // Fetches and filters orders based on static_view_type
  const fetchOrdersByStaticViewType = async (staticViewType) => {
    try {
      const response = await OrdersService.getOrdersByType(staticViewType, {
        roleId: selectedRole?.roleId || "",
      });

      // Fetch AI-allowed order IDs when calling getOrdersByType
      if (selectedRole?.roleId) {
        await fetchAndDispatchAIOrders(selectedRole.roleId);
      }

      const ordersData = response?.data?.orderSuggestions || response || {};

      // return { ordersData, countofActionNeededOrders };
      const actionNeededCount = response?.data?.actionNeededCount || 0;
      console.log(
        `Fetched orders for staticViewType: ${staticViewType}`,
        ordersData,
        `actionNeededCount: ${actionNeededCount}`,
      );

      return { ordersData, actionNeededCount };
    } catch (err) {
      console.error("Failed to fetch orders:", err);
      // return { ordersData: null, countofActionNeededOrders: 0 };
      return { ordersData: null, actionNeededCount: 0 };
    }
  };

  // Transforms API order data to chat message table format
  const transformOrdersToMessage = (ordersData) => {
    if (!ordersData) {
      return [];
    }
    const allOrders = [...ordersData]; // Only display first 6 orders

    // Return empty array if no orders after combining
    if (allOrders.length === 0) {
      return [];
    }

    // Transform to chat table format with all required columns
    // Note: API may return 'currentStage' instead of 'status'
    const transformedOrders = allOrders.map((order) => ({
      priority: order.priority || "-",
      axonId: order.axonId,
      status: order.status || order.currentStage || "-",
      subStatus: order.substatus || order.subStatus || "-",
      clientEntity:
        order.clientEntity && order.engagementName
          ? `${order.clientEntity} - ${order.engagementName}`
          : "-",
      role: order.role || "-",
      analyzer: order.analyzer || "-",
      superRegion: order.superRegion || order.super_region || "-",
      region: order.country || "-",
      country: order.country || "-",
      preparerName: order.preparerName || "-",
      reviewerName: order.reviewerName || "-",
      approverName: order.approverName || "-",
      iteration: order.iteration ?? "-",
      dueDate: order.dueDate ? formatDateForChat(order.dueDate) : "-",
      agingInDays:
        order.ageingIn ||
        order.ageing ||
        order.ageingInDays ||
        order.agingInDays ||
        "-",
      varianceReason:
        order.reasonForVariance ||
        order.reason_for_variance ||
        order.varianceReason ||
        "-",
      allocationReason:
        order.allocationReason || order.allocation_reason || "-",
      requestCreationDate: order.requestCreationDate
        ? formatDateForChat(order.requestCreationDate)
        : "-",
      guid: order.guid || "",
    }));

    return transformedOrders;
  };

  // Formats date string for chat display
  const formatDateForChat = (dateString) => {
    if (!dateString) return "-";
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return dateString;
      return date.toLocaleDateString("en-US", {
        month: "short",
        day: "2-digit",
        year: "2-digit",
      });
    } catch {
      return dateString;
    }
  };

  // Initialize with loading sequence
  useEffect(() => {
    // Skip initialization if startFresh is true
    if (startFresh) {
      return;
    }

    const initializeChat = async () => {
      // Show user message first
      if (messages.length > 0) {
        setTimeout(() => {
          setMessages([initialMessages[0]]);
        }, 500);
      } else {
        setMessages([initialMessages[0]]);
      }

      // Show loading state
      setTimeout(() => {
        setIsLoading(true);
      }, 100);

      const staticViewType = modeToStaticViewType[mode];

      // Fetch real data if mode requires orders
      if (staticViewType) {
        // const { ordersData, countofActionNeededOrders } =
        //   await fetchOrdersByStaticViewType(staticViewType);
        const fetchResult = await fetchOrdersByStaticViewType(staticViewType);

        if (fetchResult) {
          const { ordersData, actionNeededCount } = fetchResult;
          if (mode === "planMyDay") {
            setPlanMyDayOrders(ordersData);
          }
          const transformedOrders = transformOrdersToMessage(ordersData);

          // Create AI response with real data or empty message
          setTimeout(() => {
            setIsLoading(false);
            const aiMessage = {
              id: "m2",
              role: "ai",
              text:
                transformedOrders.length > 0
                  ? getAIMessageText(mode)
                  : CHAT_MESSAGES.NO_ORDERS_IN_CATEGORY,
              timestamp: Date.now(),
              orders:
                transformedOrders.length > 0 ? transformedOrders : undefined,
              //countofActionNeededOrders,
              isGenericResponse: transformedOrders.length === 0,
              actionNeededCount: actionNeededCount,
            };
            setMessages([initialMessages[0], aiMessage]);
          }, 2000);
        } else {
          // Fallback to dummy data if fetch fails
          setTimeout(() => {
            setIsLoading(false);
            setMessages(initialMessages);
          }, 2000);
        }
      } else {
        // Show AI response after loading for other modes
        setTimeout(() => {
          setIsLoading(false);
          setMessages(initialMessages);
        }, 2000);
      }
    };

    initializeChat();
  }, [mode, initialMessages, startFresh]);

  useEffect(() => {
    if (listRef.current) {
      // Use setTimeout to ensure DOM has updated before scrolling
      setTimeout(() => {
        // Scroll to the actual content height
        listRef.current.scrollTo({
          top: listRef.current.scrollHeight,
          behavior: "smooth",
        });
      }, 300);
    }
  }, [messages, isLoading]);

  // Function to fetch Order by Status data
  const fetchOrderByStatus = async () => {
    try {
      const roleId = selectedRole?.roleId || "";
      if (!roleId) {
        console.warn("No roleId available for fetching orders by status");
        return null;
      }
      const response = await OrdersService.getOrderByStatus(roleId);

      // Fetch AI-allowed order IDs independently so a failure here does not
      // block the order-by-status response from rendering.
      await fetchAndDispatchAIOrders(roleId);

      return response;
    } catch (error) {
      console.error("Failed to fetch order by status:", error);
      return null;
    }
  };

  // Map static_view_type to intro messages
  const staticViewTypeToIntro = {
    plan_my_day: CHAT_MESSAGES.PLAN_MY_DAY_INTRO,
    orders_data_triage: CHAT_MESSAGES.DATA_TRIAGE_INTRO,
    newly_allocated_orders: CHAT_MESSAGES.NEWLY_ALLOCATED_INTRO,
    aging_orders: CHAT_MESSAGES.AGEING_ORDERS_INTRO,
    high_iteration_orders: CHAT_MESSAGES.HIGH_ITERATION_ORDERS_INTRO,
    pending_review: CHAT_MESSAGES.PENDING_REVIEW_INTRO,
    my_weekly_orders: CHAT_MESSAGES.MY_WEEKLY_ORDERS_INTRO,
    awaiting_response_orders: CHAT_MESSAGES.AWAITING_RESPONSE_INTRO,
    awaiting_at_response: CHAT_MESSAGES.AWAITING_RESPONSE_INTRO,
    orders_near_duedate: CHAT_MESSAGES.ORDERS_NEARING_DUE_DATE_INTRO,
    allocation_review_request: CHAT_MESSAGES.ALLOCATION_REVIEW_REQUEST_INTRO,
    action_needed_orders: CHAT_MESSAGES.ACTION_NEEDED_ORDERS_INTRO,
    regional_leads_weekly_orders: CHAT_MESSAGES.MY_WEEKLY_ORDERS_INTRO,
  };

  // Function to add user message
  // staticViewType param allows skipping AI API when suggestion has direct mapping
  const addUserMessage = useCallback(
    async (text, staticViewType = null) => {
      console.log("[addUserMessage] Called with:", { text, staticViewType });
      console.log("[addUserMessage] staticViewTypes array:", staticViewTypes);
      console.log(
        "[addUserMessage] Is staticViewType valid?",
        staticViewType && staticViewTypes.includes(staticViewType),
      );

      const newMsg = {
        id: `m${Date.now()}`,
        role: "user",
        text: text,
        name: userName,
        timestamp: Date.now(),
      };
      setMessages((prev) => [...prev, newMsg]);

      // Show loading state
      setIsLoading(true);

      // If staticViewType is provided, skip AI API and directly fetch orders
      if (staticViewType && staticViewTypes.includes(staticViewType)) {
        // console.log(
        //   "[addUserMessage] Skipping AI API, fetching orders directly for:",
        //   staticViewType,
        // );
        try {
          // const { ordersData, countofActionNeededOrders } =
          //   await fetchOrdersByStaticViewType(staticViewType);
          const fetchResult = await fetchOrdersByStaticViewType(staticViewType);
          // console.log(
          //   "[addUserMessage] fetchOrdersByStaticViewType result:",
          //   fetchResult,
          // );
          const { ordersData, actionNeededCount } = fetchResult || {};
          const transformedOrders = transformOrdersToMessage(ordersData);

          setIsLoading(false);
          const aiResponse = {
            id: `ai${Date.now()}`,
            role: "ai",
            text:
              transformedOrders.length > 0
                ? staticViewTypeToIntro[staticViewType] ||
                  CHAT_MESSAGES.GENERIC_RESPONSE
                : CHAT_MESSAGES.NO_ORDERS_IN_CATEGORY,
            timestamp: Date.now(),
            orders:
              transformedOrders.length > 0 ? transformedOrders : undefined,
            //  countofActionNeededOrders,
            staticViewType: staticViewType,
            isGenericResponse: transformedOrders.length === 0,
            actionNeededCount: actionNeededCount,
          };
          setMessages((prev) => [...prev, aiResponse]);
          return;
        } catch (error) {
          console.error("Failed to fetch orders by static view type:", error);
          setIsLoading(false);
          const aiResponse = {
            id: `ai${Date.now()}`,
            role: "ai",
            text: CHAT_MESSAGES.GENERIC_RESPONSE,
            timestamp: Date.now(),
            isGenericResponse: true,
          };
          setMessages((prev) => [...prev, aiResponse]);
          return;
        }
      }

      // Check if this is an "Order by Status" request - call separate API (only for allowed roles)
      const normalizedText = text.toLowerCase().trim();
      const currentRoleName = selectedRole?.roleName || selectedRole?.name || "";
      
      if (normalizedText === "order by status" && isSuggestionAllowedForRole(normalizedText, currentRoleName)) {
        try {
          const orderStatusResponse = await fetchOrderByStatus();
          setIsLoading(false);

          if (orderStatusResponse) {
            const aiResponse = {
              id: `ai${Date.now()}`,
              role: "ai",
              timestamp: Date.now(),
              headerText: orderStatusResponse.headerText,
              subText: orderStatusResponse.subText,
              orderStatusData: orderStatusResponse.statusData,
            };
            setMessages((prev) => [...prev, aiResponse]);
          } else {
            // Fallback if API fails
            const aiResponse = {
              id: `ai${Date.now()}`,
              role: "ai",
              text: CHAT_MESSAGES.GENERIC_RESPONSE,
              timestamp: Date.now(),
              isGenericResponse: true,
            };
            setMessages((prev) => [...prev, aiResponse]);
          }
          return;
        } catch (error) {
          console.error("Failed to fetch order by status:", error);
          setIsLoading(false);
          const aiResponse = {
            id: `ai${Date.now()}`,
            role: "ai",
            text: CHAT_MESSAGES.GENERIC_RESPONSE,
            timestamp: Date.now(),
            isGenericResponse: true,
          };
          setMessages((prev) => [...prev, aiResponse]);
          return;
        }
      }

      // Check if this is an "Order creation pending" request - call creation failures API (only for allowed roles)
      if (normalizedText === "order creation pending" && isSuggestionAllowedForRole(normalizedText, currentRoleName)) {
        try {
          const creationFailuresResponse =
            await OrdersService.getCreationFailures();
          setIsLoading(false);

          if (
            creationFailuresResponse?.success &&
            creationFailuresResponse?.data?.failures
          ) {
            const aiResponse = {
              id: `ai${Date.now()}`,
              role: "ai",
              text: CHAT_MESSAGES.CREATION_FAILURES_INTRO,
              timestamp: Date.now(),
              creationFailures: creationFailuresResponse.data.failures,
            };
            setMessages((prev) => [...prev, aiResponse]);
          } else {
            // Fallback if no data or API fails
            const aiResponse = {
              id: `ai${Date.now()}`,
              role: "ai",
              text: CHAT_MESSAGES.NO_ORDERS_IN_CATEGORY,
              timestamp: Date.now(),
              isGenericResponse: true,
            };
            setMessages((prev) => [...prev, aiResponse]);
          }
          return;
        } catch (error) {
          console.error("Failed to fetch creation failures:", error);
          setIsLoading(false);
          const aiResponse = {
            id: `ai${Date.now()}`,
            role: "ai",
            text: CHAT_MESSAGES.GENERIC_RESPONSE,
            timestamp: Date.now(),
            isGenericResponse: true,
          };
          setMessages((prev) => [...prev, aiResponse]);
          return;
        }
      }

      // Call AI service to get response
      // console.log(
      //   "[addUserMessage] staticViewType check failed, calling AI API for:",
      //   text,
      // );
      try {
        // Fetch AI-allowed order IDs before calling AI service to ensure they are available
        if (selectedRole?.roleId) {
          await fetchAndDispatchAIOrders(selectedRole.roleId);
        }

        // Keep loading true until first chunk arrives
        // Streaming message will be created when first response arrives
        const streamingMessageId = `ai_streaming_${Date.now()}`;
        let streamingMessageCreated = false;
        let streamedText = "";
        // Track is_static_view and static_view_type from streaming response
        let streamedIsStaticView = false;
        let streamedStaticViewType = null;
        // Track thread_id and turn_id for feedback
        let streamedThreadId = null;
        let streamedTurnId = null;

        const response = await AIService.sendMessage({
          message: text,
          userId: userEmail,
          region: selectedRegion?.id || "general",

          // Handle streaming events
          onChunk: ({ eventType, data }) => {
            // Capture thread_id and turn_id from any event
            if (data) {
              if (data.thread_id !== undefined)
                streamedThreadId = data.thread_id;
              if (data.turn_id !== undefined) streamedTurnId = data.turn_id;
            }

            // Handle both "review_complete" and "result" event types
            if (
              (eventType === "review_complete" || eventType === "result") &&
              data
            ) {
              const responseText =
                data.response || data.text || data.message || "";

              // Capture is_static_view and static_view_type from the streaming response
              if (data.is_static_view !== undefined) {
                streamedIsStaticView = data.is_static_view;
              }
              if (data.static_view_type) {
                streamedStaticViewType = data.static_view_type;
              }

              if (responseText) {
                streamedText = responseText;

                // Turn off loading and create streaming message on FIRST chunk
                if (!streamingMessageCreated) {
                  setIsLoading(false);

                  // Add the streaming message WITH the initial text to avoid race condition
                  // Animation will update from empty string
                  const streamingMessage = {
                    id: streamingMessageId,
                    role: "ai",
                    text: "", // Start empty, animation will fill it
                    timestamp: Date.now(),
                    isStreaming: true,
                    isGenericResponse: true,
                    references: data.references || [],
                  };

                  // Use functional update and start animation in the same tick
                  setMessages((prev) => {
                    const newMessages = [...prev, streamingMessage];

                    // Start animation after message is added (use setTimeout to ensure state is committed)
                    setTimeout(() => {
                      animateTextWordByWord(responseText, streamingMessageId);
                    }, 0);

                    return newMessages;
                  });
                  streamingMessageCreated = true;
                } else {
                  // Message already exists, just animate
                  animateTextWordByWord(responseText, streamingMessageId);
                }
              }
            } else if (eventType === "images" && data) {
              setMessages((prev) =>
                prev.map((msg) =>
                  msg.id === streamingMessageId
                    ? {
                        ...msg,
                        retrieved_images: data.retrieved_images || [],
                        image_scores: data.image_scores || [],
                        references: data.references || msg.references || [],
                      }
                    : msg,
                ),
              );
            } else if (eventType === "suggestions" && data) {
              setMessages((prev) =>
                prev.map((msg) =>
                  msg.id === streamingMessageId
                    ? {
                        ...msg,
                        suggested_questions: data.suggested_questions || [],
                      }
                    : msg,
                ),
              );
            }
          },
        });

        // If no streaming message was created (no review_complete/result received), create final message
        if (!streamingMessageCreated) {
          setIsLoading(false);
          const finalText =
            response?.response ||
            response?.message ||
            CHAT_MESSAGES.GENERIC_RESPONSE;

          // Check if this response has is_static_view - fetch orders directly
          const isStaticView = response?.is_static_view || streamedIsStaticView;
          const staticType =
            response?.static_view_type || streamedStaticViewType;

          if (isStaticView && staticViewTypes.includes(staticType)) {
            // console.log(
            //   "[addUserMessage] Non-streaming path: is_static_view=true, fetching orders for:",
            //   staticType,
            // );
            const fetchResult = await fetchOrdersByStaticViewType(staticType);
            const { ordersData, actionNeededCount } = fetchResult || {};
            const transformedOrders = transformOrdersToMessage(ordersData);

            const aiResponse = {
              id: streamingMessageId,
              role: "ai",
              text:
                transformedOrders.length > 0
                  ? staticViewTypeToIntro[staticType] || finalText
                  : CHAT_MESSAGES.NO_ORDERS_IN_CATEGORY,
              timestamp: Date.now(),
              isStreaming: false,
              orders:
                transformedOrders.length > 0 ? transformedOrders : undefined,
              staticViewType: staticType,
              isGenericResponse: transformedOrders.length === 0,
              actionNeededCount: actionNeededCount,
            };
            setMessages((prev) => [...prev, aiResponse]);
            return;
          }

          const aiResponse = {
            id: streamingMessageId,
            role: "ai",
            text: finalText,
            timestamp: Date.now(),
            isStreaming: false,
            isGenericResponse: true,
            retrieved_images: response?.retrieved_images || [],
            suggested_questions: response?.suggested_questions || [],
            references: response?.references || [],
            checkpoint_pending: response?.checkpoint_pending || false,
            ui_fields: response?.ui_fields || [],
            ui_fields_flag: response?.ui_fields_flag || false,
            update: response?.update || false,
            thread_id: response?.thread_id || streamedThreadId,
            turn_id: response?.turn_id || streamedTurnId,
          };
          setMessages((prev) => [...prev, aiResponse]);
          return;
        }

        // Check if response has is_static_view true and valid static_view_type
        // Use both the response object and streamed variables
        const isStaticView = response?.is_static_view || streamedIsStaticView;
        const staticType = response?.static_view_type || streamedStaticViewType;

        if (isStaticView && staticViewTypes.includes(staticType)) {
          // console.log(
          //   "[addUserMessage] Streaming path: is_static_view=true, fetching orders for:",
          //   staticType,
          // );
          // Fetch orders based on static_view_type
          const fetchResult = await fetchOrdersByStaticViewType(staticType);
          const { ordersData, actionNeededCount } = fetchResult || {};
          const transformedOrders = transformOrdersToMessage(ordersData);

          // Update streaming message to final state with orders
          setMessages((prev) =>
            prev.map((msg) =>
              msg.id === streamingMessageId
                ? {
                    ...msg,
                    text:
                      transformedOrders.length > 0
                        ? staticViewTypeToIntro[staticType]
                        : CHAT_MESSAGES.NO_ORDERS_IN_CATEGORY,
                    isStreaming: false,
                    orders:
                      transformedOrders.length > 0
                        ? transformedOrders
                        : undefined,
                    staticViewType: staticType,
                    isGenericResponse: transformedOrders.length === 0,
                    retrieved_images:
                      response?.retrieved_images || msg.retrieved_images || [],
                    suggested_questions:
                      response?.suggested_questions ||
                      msg.suggested_questions ||
                      [],
                    references:
                      response?.references || msg.references || [],
                    checkpoint_pending: response?.checkpoint_pending || false,
                    ui_fields: response?.ui_fields || [],
                    ui_fields_flag: response?.ui_fields_flag || false,
                    update: response?.update || false,
                    actionNeededCount: actionNeededCount,
                    thread_id: response?.thread_id || streamedThreadId,
                    turn_id: response?.turn_id || streamedTurnId,
                  }
                : msg,
            ),
          );
        } else {
          // Standard response - finalize streaming message
          const finalText =
            streamedText ||
            response?.response ||
            response?.message ||
            CHAT_MESSAGES.GENERIC_RESPONSE;

          setMessages((prev) =>
            prev.map((msg) =>
              msg.id === streamingMessageId
                ? {
                    ...msg,
                    text: finalText,
                    isStreaming: false,
                    isGenericResponse: true,
                    retrieved_images:
                      response?.retrieved_images || msg.retrieved_images || [],
                    suggested_questions:
                      response?.suggested_questions ||
                      msg.suggested_questions ||
                      [],
                    references:
                      response?.references || msg.references || [],
                    checkpoint_pending: response?.checkpoint_pending || false,
                    ui_fields: response?.ui_fields || [],
                    ui_fields_flag: response?.ui_fields_flag || false,
                    update: response?.update || false,
                    thread_id: response?.thread_id || streamedThreadId,
                    turn_id: response?.turn_id || streamedTurnId,
                  }
                : msg,
            ),
          );
        }
      } catch (error) {
        // Check if request was aborted via kill switch - don't show error message
        if (error.name === "AbortError" || error?.aborted) {
          // console.log(
          //   "[SuperAgentChat] Request was aborted, not showing error message",
          // );
          setIsLoading(false);
          return;
        }

        console.error("Failed to fetch AI response:", error);
        setIsLoading(false);
        const aiResponse = {
          id: `ai${Date.now()}`,
          role: "ai",
          text: CHAT_MESSAGES.GENERIC_RESPONSE,
          timestamp: Date.now(),
          isGenericResponse: true,
        };
        setMessages((prev) => [...prev, aiResponse]);
      }
    },
    [user, selectedRegion, userEmail, userName, animateTextWordByWord],
  );

  // Expose the addMessage function via onSendMessage
  useEffect(() => {
    if (onSendMessage) {
      onSendMessage.current = addUserMessage;
    }
  }, [onSendMessage, addUserMessage]);

  // Expose the startNewChat function via onNewChatRequest
  useEffect(() => {
    if (onNewChatRequest) {
      onNewChatRequest.current = async (text, newMode) => {
        // Add the user message to existing chat trail
        const newMsg = {
          id: `m${Date.now()}`,
          role: "user",
          text: text,
          name: userName,
          timestamp: Date.now(),
        };
        setMessages((prev) => [...prev, newMsg]);

        // Show loading state
        setIsLoading(true);

        // Map newMode to static_view_type
        const modeToStaticViewType = {
          planMyDay: "plan_my_day",
          ordersInDataTriage: "orders_data_triage",
          newlyAllocatedOrders: "newly_allocated_orders",
        };

        const staticViewType = modeToStaticViewType[newMode];

        // Fetch real data if newMode requires orders
        if (staticViewType) {
          const fetchResult = await fetchOrdersByStaticViewType(staticViewType);

          if (fetchResult) {
            const { ordersData, actionNeededCount } = fetchResult;
            if (newMode === "planMyDay") {
              setPlanMyDayOrders(ordersData);
            }
            const transformedOrders = transformOrdersToMessage(ordersData);

            // Create AI response with real data or empty message - append to existing trail
            setTimeout(() => {
              setIsLoading(false);
              const aiMessage = {
                id: `ai${Date.now()}`,
                role: "ai",
                text:
                  transformedOrders.length > 0
                    ? getAIMessageText(newMode)
                    : CHAT_MESSAGES.NO_ORDERS_IN_CATEGORY,
                timestamp: Date.now(),
                orders:
                  transformedOrders.length > 0 ? transformedOrders : undefined,
                isGenericResponse: transformedOrders.length === 0,
                actionNeededCount: actionNeededCount,
              };
              setMessages((prev) => [...prev, aiMessage]);
            }, 2000);
          } else {
            // Fallback response if fetch fails - append to existing trail
            setTimeout(() => {
              setIsLoading(false);
              const aiMessage = {
                id: `ai${Date.now()}`,
                role: "ai",
                text: CHAT_MESSAGES.GENERIC_RESPONSE,
                timestamp: Date.now(),
                isGenericResponse: true,
              };
              setMessages((prev) => [...prev, aiMessage]);
            }, 2000);
          }
        } else {
          // For modes without staticViewType (like mandatoryFieldsJE, reviewerUS), call AI service
          try {
            // Fetch AI-allowed order IDs before calling AI service to ensure they are available
            if (selectedRole?.roleId) {
              await fetchAndDispatchAIOrders(selectedRole.roleId);
            }

            // Keep loading true until first chunk arrives
            const streamingMsgId = `ai_streaming_${Date.now()}`;
            let streamingMessageCreated = false;
            let streamedText = "";

            const response = await AIService.sendMessage({
              message: text,
              userId: userEmail,
              region: selectedRegion?.id || "general",

              // Handle streaming events
              onChunk: ({ eventType, data }) => {
                if (eventType === "review_complete" && data) {
                  const responseText =
                    data.response || data.text || data.message || "";
                  if (responseText) {
                    streamedText = responseText;

                    // Turn off loading and create streaming message on FIRST chunk
                    if (!streamingMessageCreated) {
                      setIsLoading(false);
                      const streamingMsg = {
                        id: streamingMsgId,
                        role: "ai",
                        text: "",
                        timestamp: Date.now(),
                        isStreaming: true,
                        isGenericResponse: true,
                      };
                      streamingMessageCreated = true;
                      setMessages((prev) => {
                        // Use setTimeout to ensure state update completes before animation starts
                        setTimeout(() => {
                          animateTextWordByWord(responseText, streamingMsgId);
                        }, 0);
                        return [...prev, streamingMsg];
                      });
                    } else {
                      // Message already exists, just animate
                      animateTextWordByWord(responseText, streamingMsgId);
                    }
                  }
                } else if (eventType === "images" && data) {
                  setMessages((prev) =>
                    prev.map((msg) =>
                      msg.id === streamingMsgId
                        ? {
                            ...msg,
                            retrieved_images: data.retrieved_images || [],
                            image_scores: data.image_scores || [],
                            references: data.references || msg.references || [],
                          }
                        : msg,
                    ),
                  );
                } else if (eventType === "suggestions" && data) {
                  setMessages((prev) =>
                    prev.map((msg) =>
                      msg.id === streamingMsgId
                        ? {
                            ...msg,
                            suggested_questions: data.suggested_questions || [],
                          }
                        : msg,
                    ),
                  );
                }
              },
            });

            // If no streaming message was created (no review_complete), create final message
            if (!streamingMessageCreated) {
              setIsLoading(false);
              const finalText =
                response?.response ||
                response?.message ||
                CHAT_MESSAGES.GENERIC_RESPONSE;
              const aiResponse = {
                id: streamingMsgId,
                role: "ai",
                text: finalText,
                timestamp: Date.now(),
                isStreaming: false,
                isGenericResponse: true,
                retrieved_images: response?.retrieved_images || [],
                suggested_questions: response?.suggested_questions || [],
                references: response?.references || [],
                checkpoint_pending: response?.checkpoint_pending || false,
                ui_fields: response?.ui_fields || [],
              };
              setMessages((prev) => [...prev, aiResponse]);
              return;
            }

            // Check if response has is_static_view true and valid static_view_type
            if (
              response?.is_static_view &&
              staticViewTypes.includes(response?.static_view_type)
            ) {
              const fetchResult = await fetchOrdersByStaticViewType(
                response.static_view_type,
              );
              const { ordersData, actionNeededCount } = fetchResult || {};
              const transformedOrders = transformOrdersToMessage(ordersData);

              setMessages((prev) =>
                prev.map((msg) =>
                  msg.id === streamingMsgId
                    ? {
                        ...msg,
                        text:
                          transformedOrders.length > 0
                            ? staticViewTypeToIntro[response.static_view_type]
                            : CHAT_MESSAGES.NO_ORDERS_IN_CATEGORY,
                        isStreaming: false,
                        orders:
                          transformedOrders.length > 0
                            ? transformedOrders
                            : undefined,
                        staticViewType: response.static_view_type,
                        isGenericResponse: transformedOrders.length === 0,
                        retrieved_images:
                          response?.retrieved_images ||
                          msg.retrieved_images ||
                          [],
                        suggested_questions:
                          response?.suggested_questions ||
                          msg.suggested_questions ||
                          [],
                        references:
                          response?.references || msg.references || [],
                        checkpoint_pending:
                          response?.checkpoint_pending || false,
                        ui_fields: response?.ui_fields || [],
                        actionNeededCount: actionNeededCount,
                      }
                    : msg,
                ),
              );
            } else {
              // Standard response - finalize streaming message
              const finalText =
                streamedText ||
                response?.response ||
                response?.message ||
                CHAT_MESSAGES.GENERIC_RESPONSE;

              setMessages((prev) =>
                prev.map((msg) =>
                  msg.id === streamingMsgId
                    ? {
                        ...msg,
                        text: finalText,
                        isStreaming: false,
                        isGenericResponse: true,
                        retrieved_images:
                          response?.retrieved_images ||
                          msg.retrieved_images ||
                          [],
                        suggested_questions:
                          response?.suggested_questions ||
                          msg.suggested_questions ||
                          [],
                        references:
                          response?.references || msg.references || [],
                        checkpoint_pending:
                          response?.checkpoint_pending || false,
                        ui_fields: response?.ui_fields || [],
                      }
                    : msg,
                ),
              );
            }
          } catch (error) {
            // Check if request was aborted via kill switch - don't show error message
            if (error.name === "AbortError" || error?.aborted) {
              // console.log(
              //   "[SuperAgentChat] Request was aborted, not showing error message",
              // );
              setIsLoading(false);
              return;
            }

            console.error("Failed to fetch AI response:", error);
            setIsLoading(false);
            const aiResponse = {
              id: `ai${Date.now()}`,
              role: "ai",
              text: CHAT_MESSAGES.GENERIC_RESPONSE,
              timestamp: Date.now(),
              isGenericResponse: true,
            };
            setMessages((prev) => [...prev, aiResponse]);
          }
        }
      };
    }
  }, [onNewChatRequest, user, selectedRegion, userEmail, userName]);

  // Handle status click from OrderStatusTableAICard
  const handleStatusClick = useCallback(
    async (statusItem) => {
      // Add user message with the selected status
      const statusText = `Show orders with status: ${statusItem.status}`;

      const newMsg = {
        id: `m${Date.now()}`,
        role: "user",
        text: statusText,
        name: userName,
        timestamp: Date.now(),
      };
      setMessages((prev) => [...prev, newMsg]);

      // Show loading state
      setIsLoading(true);

      try {
        const roleId = selectedRole?.roleId || "";
        const stageId = statusItem.stageId;
        const isMainStage = statusItem.isMainStage || false;

        if (!roleId || !stageId) {
          console.warn(
            "Missing roleId or stageId for fetching orders by status",
          );
          setIsLoading(false);
          const aiResponse = {
            id: `ai${Date.now()}`,
            role: "ai",
            text: CHAT_MESSAGES.GENERIC_RESPONSE,
            timestamp: Date.now(),
            isGenericResponse: true,
          };
          setMessages((prev) => [...prev, aiResponse]);
          return;
        }

        // Call the API to get orders by status ID
        const response = await OrdersService.getOrdersByStatusId(
          roleId,
          stageId,
          isMainStage,
        );

        // Fetch AI-allowed order IDs independently
        await fetchAndDispatchAIOrders(roleId);

        setIsLoading(false);

        // Extract orders from response - API returns { success, data: { orderSuggestions: [...] } }
        const ordersData = response?.data?.orderSuggestions || [];

        if (ordersData.length > 0) {
          const transformedOrders = transformOrdersToMessage(ordersData);
          const aiResponse = {
            id: `ai${Date.now()}`,
            role: "ai",
            text: `Here are the orders with status "${statusItem.status}":`,
            orders: transformedOrders,
            timestamp: Date.now(),
          };
          setMessages((prev) => [...prev, aiResponse]);
        } else {
          // No orders found for this status
          const aiResponse = {
            id: `ai${Date.now()}`,
            role: "ai",
            text: `No orders found with status "${statusItem.status}".`,
            timestamp: Date.now(),
            isGenericResponse: true,
          };
          setMessages((prev) => [...prev, aiResponse]);
        }
      } catch (error) {
        console.error("Failed to fetch orders by status ID:", error);
        setIsLoading(false);
        const aiResponse = {
          id: `ai${Date.now()}`,
          role: "ai",
          text: CHAT_MESSAGES.GENERIC_RESPONSE,
          timestamp: Date.now(),
          isGenericResponse: true,
        };
        setMessages((prev) => [...prev, aiResponse]);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [userName, selectedRole],
  );

  // Handle checkpoint confirm - sends resume_input to AI
  const handleCheckpointConfirm = useCallback(
    async (messageId, resumeInput) => {
      // Set submitting state for this message
      setCheckpointState({
        messageId,
        isSubmitting: true,
        submitError: null,
        submitSuccess: false,
      });

      // Find the previous user query before this checkpoint AI message
      const messageIndex = messages.findIndex((m) => m.id === messageId);
      let previousQuery = "";
      if (messageIndex > 0) {
        // Look backwards from the checkpoint message to find the last user message
        for (let i = messageIndex - 1; i >= 0; i--) {
          if (messages[i].role === "user") {
            previousQuery = messages[i].text || "";
            break;
          }
        }
      }

      // Keep checkpoint submitting state visible until first chunk arrives
      const streamingMsgId = `ai_streaming_${Date.now()}`;
      let streamingMessageCreated = false;

      try {
        let streamedText = "";

        // Call AI service with resume_input action and streaming
        const response = await AIService.sendMessage({
          message: previousQuery || "resume_checkpoint",
          userId: userEmail,
          region: selectedRegion?.id || "general",
          resumeInput: resumeInput,

          // Handle streaming events
          onChunk: ({ eventType, data }) => {
            if (eventType === "review_complete" && data) {
              const responseText =
                data.response || data.text || data.message || "";
              if (responseText) {
                streamedText = responseText;

                // Create streaming message on FIRST chunk
                if (!streamingMessageCreated) {
                  const streamingMsg = {
                    id: streamingMsgId,
                    role: "ai",
                    text: "",
                    timestamp: Date.now(),
                    isStreaming: true,
                  };
                  streamingMessageCreated = true;
                  setMessages((prev) => {
                    // Use setTimeout to ensure state update completes before animation starts
                    setTimeout(() => {
                      animateTextWordByWord(responseText, streamingMsgId);
                    }, 0);
                    return [...prev, streamingMsg];
                  });
                } else {
                  // Message already exists, just animate
                  animateTextWordByWord(responseText, streamingMsgId);
                }
              }
            } else if (eventType === "images" && data) {
              setMessages((prev) =>
                prev.map((msg) =>
                  msg.id === streamingMsgId
                    ? {
                        ...msg,
                        retrieved_images: data.retrieved_images || [],
                        image_scores: data.image_scores || [],
                        references: data.references || msg.references || [],
                      }
                    : msg,
                ),
              );
            } else if (eventType === "suggestions" && data) {
              setMessages((prev) =>
                prev.map((msg) =>
                  msg.id === streamingMsgId
                    ? {
                        ...msg,
                        suggested_questions: data.suggested_questions || [],
                      }
                    : msg,
                ),
              );
            }
          },
        });

        // Update checkpoint state to success
        setCheckpointState({
          messageId,
          isSubmitting: false,
          submitError: null,
          submitSuccess: true,
        });

        // If no streaming message was created, create final message
        if (!streamingMessageCreated) {
          const finalText =
            response?.response ||
            response?.message ||
            "Details confirmed successfully.";
          const aiResponse = {
            id: streamingMsgId,
            role: "ai",
            text: finalText,
            timestamp: Date.now(),
            isStreaming: false,
            isGenericResponse: true,
            retrieved_images: response?.retrieved_images || [],
            suggested_questions: response?.suggested_questions || [],
            references: response?.references || [],
            checkpoint_pending: response?.checkpoint_pending || false,
            ui_fields: response?.ui_fields || [],
          };
          setMessages((prev) => [...prev, aiResponse]);
        } else {
          // Finalize the streaming message
          const finalText =
            streamedText ||
            response?.response ||
            response?.message ||
            "Details confirmed successfully.";

          setMessages((prev) =>
            prev.map((msg) =>
              msg.id === streamingMsgId
                ? {
                    ...msg,
                    text: finalText,
                    isStreaming: false,
                    isGenericResponse: true,
                    retrieved_images:
                      response?.retrieved_images || msg.retrieved_images || [],
                    suggested_questions:
                      response?.suggested_questions ||
                      msg.suggested_questions ||
                      [],
                    references:
                      response?.references || msg.references || [],
                    checkpoint_pending: response?.checkpoint_pending || false,
                    ui_fields: response?.ui_fields || [],
                  }
                : msg,
            ),
          );
        }

        // Reset checkpoint state after a delay
        setTimeout(() => {
          setCheckpointState({
            messageId: null,
            isSubmitting: false,
            submitError: null,
            submitSuccess: false,
          });
        }, 2000);
      } catch (error) {
        console.error("Failed to confirm checkpoint:", error);
        // Remove the streaming message on error if it was created
        if (streamingMessageCreated) {
          setMessages((prev) =>
            prev.filter((msg) => msg.id !== streamingMsgId),
          );
        }
        setCheckpointState({
          messageId,
          isSubmitting: false,
          submitError: "Failed to confirm details. Please try again.",
          submitSuccess: false,
        });
      }
    },
    [userEmail, selectedRegion, messages, animateTextWordByWord],
  );

  return (
    <div className="super-agent-chat-display" ref={listRef}>
      {messages.map((m) => {
        if (m.role === "user") {
          return <SuperChatUser key={m.id} message={m} userName={userName} />;
        }

        if (m.role === "ai") {
          // Check if this message has checkpoint pending
          const isCheckpointMessage =
            m.checkpoint_pending && m.ui_fields?.length > 0;
          const isCurrentCheckpoint = checkpointState.messageId === m.id;

          return (
            <SuperChatAI
              key={m.id}
              message={m}
              onAddUserMessage={addUserMessage}
              onStatusClick={handleStatusClick}
              onCheckpointConfirm={
                isCheckpointMessage
                  ? (resumeInput) => handleCheckpointConfirm(m.id, resumeInput)
                  : undefined
              }
              isCheckpointSubmitting={
                isCurrentCheckpoint ? checkpointState.isSubmitting : false
              }
              checkpointSubmitError={
                isCurrentCheckpoint ? checkpointState.submitError : null
              }
              checkpointSubmitSuccess={
                isCurrentCheckpoint ? checkpointState.submitSuccess : false
              }
            />
          );
        }

        return null;
      })}

      {isLoading && (
        <div className="super-agent-bubble ai-left">
          <div className="super-agent-ai-header">
            <img
              src={aiChatlogo}
              alt={CHAT.AXON_AI}
              className="super-agent-ai-logo"
            />
            <div className="super-agent-ai-title">
              <span>{CHAT.AXON_AI}</span>
            </div>
          </div>
          <div className="super-agent-loading">
            <div className="super-agent-loading-text">
              {CHAT_MESSAGES.COLLECTING_DATA}
            </div>
            <div className="super-agent-loading-bar">
              <div className="super-agent-loading-progress"></div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SuperAgentChat;
